type SignalCallback = (parameters?: any) => void;

interface PendingRequest {
	onSuccess?: SignalCallback;
	onError?: SignalCallback;
}

export default class SignalClient {
	pending: Record<string, PendingRequest> = {};
	tryReconnect = true;
	connectionState = '';
	socket?: WebSocket;
	serverURL?: string;
	pingInterval?: any;

	onConnectionStateChange: SignalCallback = () => {};
	onContactUpdate: SignalCallback = () => {};
	onRequestCall: SignalCallback = () => {};
	onCallAccepted: SignalCallback = () => {};
	onCallEnded: SignalCallback = () => {};
	onRtcOffer: SignalCallback = () => {};
	onRtcAnswer: SignalCallback = () => {};
	onRtcIceCandidate: SignalCallback = () => {};
	onRtcCustom: SignalCallback = () => {};

	connect(serverURL?: string | null, env?: string) {
		this.serverURL = serverURL || this.getSignalServer(env);
		this.tryReconnect = true;
		this.flushPending();
		console.info('SIGNAL.DEBUG connect', { serverURL: this.serverURL, env: env });
		this.updateConnectionState('TRYING_TO_CONNECT');
		this.socket = new WebSocket(this.serverURL);

		this.socket.onopen = () => {
			console.info('SIGNAL.DEBUG open', { serverURL: this.serverURL });
			this.pingInterval = setInterval(() => { this.send({ op: 'ping' }); }, 20000);
			this.updateConnectionState('CONNECTED');
		};
		this.socket.onmessage = (event) => {
			this.handleMsg(event.data);
		};
		this.socket.onerror = () => {
			console.error('SIGNAL.DEBUG error', { serverURL: this.serverURL });
			this.flushPending({ code: 'E_CONNECTION_LOST' });
		};
		this.socket.onclose = (event) => {
			console.warn('SIGNAL.DEBUG close', {
				code: event.code,
				reason: event.reason,
				wasClean: event.wasClean,
				tryReconnect: this.tryReconnect
			});
			clearInterval(this.pingInterval);
			if (this.tryReconnect === true) {
				setTimeout(() => { this.connect(this.serverURL); }, 3000);
				this.updateConnectionState('TRYING_TO_CONNECT');
			} else {
				this.updateConnectionState('DISCONNECTED');
			}
		};
		return this;
	}

	getSignalServer(env?: string) {
		if (env === 'live') return 'wss://signal.sentab.com';
		if (env === 'stage') return 'wss://signal-stage.sentab.com';
		return 'wss://signal-test.sentab.com';
	}

	getConnectionState() {
		return this.connectionState;
	}

	disconnect() {
		this.tryReconnect = false;
		clearInterval(this.pingInterval);
		if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
			this.socket.close();
		}
		this.flushPending({ code: 'E_CONNECTION_LOST' });
		this.updateConnectionState('DISCONNECTED');
		return this;
	}

	authenticate(region, userId, password, platform, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'authenticate', parameters: { region, userId, password, platform } }, onSuccess, onError);
		return this;
	}

	requestCall(toEndpoint, audioOnly, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'requestCall', parameters: { toEndpoint, audioOnly } }, onSuccess, onError);
		return this;
	}

	acceptCall(onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'acceptCall' }, onSuccess, onError);
		return this;
	}

	declineCall(detail, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'declineCall', parameters: { detail } }, onSuccess, onError);
		return this;
	}

	hangUpCall(detail, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'hangUpCall', parameters: { detail } }, onSuccess, onError);
		return this;
	}

	rtcOffer(offer, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'rtcOffer', parameters: { offer } }, onSuccess, onError);
		return this;
	}

	rtcAnswer(answer, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'rtcAnswer', parameters: { answer } }, onSuccess, onError);
		return this;
	}

	rtcIceCandidate(iceCandidate, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'rtcIceCandidate', parameters: { iceCandidate } }, onSuccess, onError);
		return this;
	}

	rtcCustom(parameters, onSuccess?: SignalCallback, onError?: SignalCallback) {
		this.send({ op: 'rtcCustom', parameters }, onSuccess, onError);
		return this;
	}

	private getId() {
		var text = '';
		var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		for (var i = 0; i < 40; i++) {
			text += possible.charAt(Math.floor(Math.random() * possible.length));
		}
		return text;
	}

	private send(outObj: any, onSuccess?: SignalCallback, onError?: SignalCallback) {
		outObj.id = this.getId();
		this.pending[outObj.id] = { onSuccess, onError };

		if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
			console.error('SIGNAL.DEBUG send.blocked', {
				op: outObj.op,
				readyState: this.socket ? this.socket.readyState : null
			});
			this.failPending(outObj.id, { code: 'E_CONNECTION_LOST' });
			return;
		}

		try {
			var outStr = JSON.stringify(outObj);
			console.log(' > ' + outStr);
			this.socket.send(outStr);
		} catch (e) {
			this.failPending(outObj.id, { code: 'E_CONNECTION_LOST' });
		}
	}

	private handleMsg(inStr: string) {
		console.log(' < ' + inStr);
		var inObj = JSON.parse(inStr);
		switch (inObj.result) {
			case 'ok':
				this.handlePendingSuccess(inObj);
				break;
			case 'error':
				this.handlePendingError(inObj);
				break;
			default:
				this.handleEvent(inObj);
		}
	}

	private handlePendingSuccess(inObj: any) {
		var stub = this.pending[inObj.id];
		if (stub) {
			this.doCallback(stub.onSuccess, inObj.parameters);
			delete this.pending[inObj.id];
		}
	}

	private handlePendingError(inObj: any) {
		var stub = this.pending[inObj.id];
		if (stub) {
			this.doCallback(stub.onError, inObj.parameters);
			delete this.pending[inObj.id];
		}
	}

	private handleEvent(inObj: any) {
		switch (inObj.op) {
			case 'contactUpdate':
				this.doCallback(this.onContactUpdate, inObj.parameters);
				break;
			case 'requestCall':
				this.doCallback(this.onRequestCall, inObj.parameters);
				break;
			case 'callAccepted':
				this.doCallback(this.onCallAccepted, inObj.parameters);
				break;
			case 'callEnded':
				this.doCallback(this.onCallEnded, inObj.parameters);
				break;
			case 'rtcOffer':
				this.doCallback(this.onRtcOffer, inObj.parameters);
				break;
			case 'rtcAnswer':
				this.doCallback(this.onRtcAnswer, inObj.parameters);
				break;
			case 'rtcIceCandidate':
				this.doCallback(this.onRtcIceCandidate, inObj.parameters);
				break;
			case 'rtcCustom':
				this.doCallback(this.onRtcCustom, inObj.parameters);
				break;
			default:
				console.log("ERROR: Unhandled op - '" + inObj.op + "'.");
		}
	}

	private failPending(id: string, parameters: any) {
		var stub = this.pending[id];
		if (stub) {
			this.doCallback(stub.onError, parameters);
			delete this.pending[id];
		}
	}

	private flushPending(parameters: any = { code: 'E_CONNECTION_LOST' }) {
		for (var msgId of Object.keys(this.pending)) {
			this.failPending(msgId, parameters);
		}
	}

	private updateConnectionState(newConnectionState: string) {
		if (this.connectionState !== newConnectionState) {
			this.connectionState = newConnectionState;
			this.onConnectionStateChange(newConnectionState);
		}
	}

	private doCallback(callback?: SignalCallback, event?: any) {
		if (!callback) return;
		if (typeof callback === 'function') {
			callback(event);
		} else {
			console.log('ERROR: Callback was not a function.');
		}
	}
}
